import { ReactNode, useCallback, useRef, useState } from 'react';
import { marked } from 'marked';
import { getAnalyticsBuilderData } from '@/api';
import AnalyticsBuilderExportModal from './AnalyticsBuilderExportModal';
import AnalyticsBuilderGenerateModal from './AnalyticsBuilderGenerateModal';
import AnalyticsBuilderReportModal from './AnalyticsBuilderReportModal';
import AnalyticsBuilderSetupModal from './AnalyticsBuilderSetupModal';
import type {
  AnalyticsBuilderFieldOption,
  AnalyticsBuilderFieldSetType,
  AnalyticsBuilderFieldType,
  AnalyticsBuilderInputs,
  AnalyticsBuilderReportData,
  AnalyticsBuilderReportSectionData,
  AnalyticsBuilderResolvedTemplateStructureItem,
  AnalyticsBuilderResolvedTemplateType,
  AnalyticsBuilderTemplateType,
} from '@/types';
import { AnalyticsBuilderFieldTypeType } from '@/types';

type AnalyticsBuilderMode = 'setup' | 'generating' | 'report';

type AnalyticsBuilderProps = {
  prompt: AnalyticsBuilderTemplateType;
  fieldSets: AnalyticsBuilderFieldSetType[];
  promptIndex?: number;
  children: (openPrompt: () => void) => ReactNode;
};

const generateIdFromDisplayName = (displayName: string) =>
  displayName
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getInputsDisplayValue = (inputs: AnalyticsBuilderInputs, template: AnalyticsBuilderResolvedTemplateType) => {
  const findOptionByValue = (
    options: AnalyticsBuilderFieldOption[] | undefined,
    value: string,
  ): AnalyticsBuilderFieldOption | undefined => {
    for (const option of options || []) {
      if (option.id === value || option.key === value) {
        return option;
      }

      const childOption = findOptionByValue(option.options, value);

      if (childOption) {
        return childOption;
      }
    }

    return undefined;
  };

  const values = template.fieldSet.fields
    .map((field) => {
      if (field.type === AnalyticsBuilderFieldTypeType.Hidden) {
        return null;
      }

      const value = inputs[field.queryParameterName]?.trim();

      if (!value) {
        return null;
      }

      if (
        field.type === AnalyticsBuilderFieldTypeType.TaxonomyDropdown ||
        field.type === AnalyticsBuilderFieldTypeType.Dropdown
      ) {
        return findOptionByValue(field.options, value)?.name || value;
      }

      return value;
    })
    .filter(Boolean);

  return values.length ? values.join(', ') : 'Selected criteria';
};

const parseMarkdown = (md: string): string => {
  const renderer = new marked.Renderer();
  const normalizedMarkdown = md
    .split('\n')
    .map((line) =>
      line.replace(/^ {1,}(#{1,6}\s+)/, '$1').replace(/^ {1,}((?:[-*+]|\d+[.)])\s+)/, '$1'),
    )
    .join('\n');
  const markdownWithSourceRefs = normalizedMarkdown.replace(/\[source(\d+)\]/gi, '<sup class="text-black">[$1]</sup>');

  renderer.link = ({ href, title, tokens }) => {
    const text = marked.Parser.parseInline(tokens);
    const titleAttr = title ? ` title="${title}"` : '';

    return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
  };

  const html = marked.parse(markdownWithSourceRefs, { renderer }) as string;

  return html;
};

const fetchAnalyticsBuilderSection = async (
  structureItem: AnalyticsBuilderResolvedTemplateStructureItem,
  template: AnalyticsBuilderResolvedTemplateType,
  inputs: AnalyticsBuilderInputs,
): Promise<AnalyticsBuilderReportSectionData> => {
  const response = await getAnalyticsBuilderData({
    urlSegment: [template.urlSegement, structureItem.urlSegement],
    queryParams: inputs,
  });

  if (response.status === 'Failed') {
    throw new Error(response.errormessage || 'Analytics Builder section generation failed.');
  }

  return {
    id: structureItem.id,
    title: structureItem.displayName,
    html: parseMarkdown(response.data.response),
    sources: response.data.sources,
    status: 'done',
  };
};

/**
 * Runs `worker` over `items` with ramped concurrency: item 0 fires alone, then once it
 * settles (or after `rampDelayMs`, whichever comes first) the pool ramps up to
 * `maxConcurrency` concurrent workers for the remaining items.
 */
const runRampedGeneration = <T,>(
  items: T[],
  worker: (item: T, index: number) => Promise<void>,
  isCancelled: () => boolean,
  options?: { rampDelayMs?: number; maxConcurrency?: number },
): void => {
  const rampDelayMs = options?.rampDelayMs ?? 10000;
  const maxConcurrency = options?.maxConcurrency ?? 3;

  if (items.length === 0) {
    return;
  }

  let nextIndex = 1;
  let activeCount = 0;
  let rampedUp = items.length <= 1;
  let rampTimer: ReturnType<typeof setTimeout> | null = null;

  const start = (index: number) => {
    activeCount += 1;
    worker(items[index], index).finally(() => {
      activeCount -= 1;
      onSettle();
    });
  };

  const fillPool = () => {
    while (!isCancelled() && activeCount < maxConcurrency && nextIndex < items.length) {
      start(nextIndex);
      nextIndex += 1;
    }
  };

  const ramp = () => {
    if (rampedUp || isCancelled()) {
      return;
    }

    rampedUp = true;

    if (rampTimer) {
      clearTimeout(rampTimer);
      rampTimer = null;
    }

    fillPool();
  };

  function onSettle() {
    if (isCancelled()) {
      return;
    }

    if (!rampedUp) {
      ramp();
      return;
    }

    fillPool();
  }

  start(0);

  if (!rampedUp) {
    rampTimer = setTimeout(ramp, rampDelayMs);
  }
};

const AnalyticsBuilder = ({ prompt, fieldSets, promptIndex = 0, children }: AnalyticsBuilderProps) => {
  const [inputs, setInputs] = useState<AnalyticsBuilderInputs | null>(null);
  const [reportData, setReportData] = useState<AnalyticsBuilderReportData | null>(null);
  const [mode, setMode] = useState<AnalyticsBuilderMode | null>(null);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const generationTokenRef = useRef(0);
  const firstAreaIdRef = useRef<string | null>(null);
  const promptId = prompt.urlSegement || generateIdFromDisplayName(prompt.displayName) || `prompt-${promptIndex + 1}`;

  const promptFieldSets = (prompt.fieldSetNames || [])
    .map((fieldSetName) => fieldSets.find((fieldSet) => fieldSet.name === fieldSetName))
    .filter((fieldSet): fieldSet is AnalyticsBuilderFieldSetType => Boolean(fieldSet));

  const promptFieldSet: AnalyticsBuilderFieldSetType = {
    name: prompt.fieldSetNames?.join(',') || promptId,
    fields: promptFieldSets.flatMap((fieldSet) => fieldSet.fields),
  };

  const setupFields = (prompt: AnalyticsBuilderTemplateType, fieldSet: AnalyticsBuilderFieldSetType) => {
    if (prompt.urlSegement === 'cityprofilepestanalysis') {
      return [
        {
          queryParameterName: 'cityName',
          type: AnalyticsBuilderFieldTypeType.AutocompleteCity,
        },
        { queryParameterName: 'cityId', type: AnalyticsBuilderFieldTypeType.Hidden },
      ];
    } else if (prompt.urlSegement === 'countrymacroeconomicdata') {
      const options =
        fieldSet.fields
          .find((field) => field.queryParameterName === 'geographyId')
          ?.options?.filter((option) => !['Global'].includes(option.name))
          .map((option) => ({
            ...option,
            disabled: true,
            options:
              option.options?.map((subOption) => ({
                ...subOption,
                options: undefined,
              })) || [],
          })) || [];


      return [
        {
          queryParameterName: 'geographyId',
          type: AnalyticsBuilderFieldTypeType.TaxonomyDropdown,
          options,
        },
        { queryParameterName: 'geographyName', type: AnalyticsBuilderFieldTypeType.Hidden },
      ];
    } else if (prompt.urlSegement === 'companyrdinnovationinsights') {
      return [
        {
          queryParameterName: 'companyName',
          type: AnalyticsBuilderFieldTypeType.AutocompleteCompany,
        },
        { queryParameterName: 'companyId', type: AnalyticsBuilderFieldTypeType.Hidden },
      ];
    } else if (['marketentryenvironment', 'fiveforcesanalysis', 'technologyimpact', 'supplychainstrategy'].includes(prompt.urlSegement)) {
      return fieldSet.fields.map((field: AnalyticsBuilderFieldType) => {
        if (field.queryParameterName === 'currency') {
          return {
            ...field,
            type: AnalyticsBuilderFieldTypeType.Dropdown,
            options: [
              {
                id: 'USD',
                key: 'USD',
                name: 'USD',
              },
              {
                id: 'EURO',
                key: 'EURO',
                name: 'EUR',
              },
              {
                id: 'LCU',
                key: 'LCU',
                name: 'LCU',
              },
            ],
          };
        } else if (field.queryParameterName === 'industry') {
          return {
            ...field,
            queryParameterName: 'industryId',
          };
        } else if (field.queryParameterName === 'geography') {
          return {
            ...field,
            queryParameterName: 'geographyId',
          };
        }

        return field;
      });
    }
    return fieldSet.fields;
  };

  const resolvedPrompt: AnalyticsBuilderResolvedTemplateType = {
    ...prompt,
    fieldSet: {
      ...promptFieldSet,
      fields: setupFields(prompt, promptFieldSet),
    },

    id: promptId,
    areas: prompt.areas.map((area, areaIndex) => ({
      ...area,
      id: area.id || generateIdFromDisplayName(area.displayName) || `${promptId}-area-${areaIndex + 1}`,
    })),
  };

  const cancelGeneration = () => {
    generationTokenRef.current += 1;
  };

  const applySectionResult = useCallback((token: number, section: AnalyticsBuilderReportSectionData) => {
    if (generationTokenRef.current !== token) {
      return;
    }

    setReportData((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        sections: current.sections.map((existing) => (existing.id === section.id ? section : existing)),
      };
    });

    if (section.id === firstAreaIdRef.current) {
      setMode((currentMode) => (currentMode === 'generating' ? 'report' : currentMode));
    }
  }, []);

  const openPrompt = () => {
    cancelGeneration();
    setInputs(null);
    setReportData(null);
    setIsExportOpen(false);
    setMode('setup');
  };

  const generateReport = (nextInputs: AnalyticsBuilderInputs) => {
    const token = ++generationTokenRef.current;
    firstAreaIdRef.current = resolvedPrompt.areas[0]?.id ?? null;

    const subject = getInputsDisplayValue(nextInputs, resolvedPrompt);

    setInputs(nextInputs);
    setIsExportOpen(false);
    setReportData({
      id: `${resolvedPrompt.id}-${Date.now()}`,
      displayName: resolvedPrompt.displayName,
      subject,
      title: `${subject}: ${resolvedPrompt.displayName}`,
      description: resolvedPrompt.description,
      sections: resolvedPrompt.areas.map((area) => ({
        id: area.id,
        title: area.displayName,
        status: 'loading',
      })),
    });
    setMode('generating');

    const runArea = async (area: (typeof resolvedPrompt.areas)[number]) => {
      try {
        const section = await fetchAnalyticsBuilderSection(area, resolvedPrompt, nextInputs);
        applySectionResult(token, section);
      } catch {
        applySectionResult(token, {
          id: area.id,
          title: area.displayName,
          html: '<p>This section could not be generated. You can continue with the available report content.</p>',
          sources: [],
          status: 'failed',
        });
      }
    };

    runRampedGeneration(resolvedPrompt.areas, runArea, () => generationTokenRef.current !== token);
  };

  const closeSetupModal = () => {
    if (mode !== 'setup') {
      return;
    }

    setIsExportOpen(false);
    setInputs(null);
    setReportData(null);
    setMode(null);
  };

  const closeGenerateModal = () => {
    if (mode !== 'generating') {
      return;
    }

    cancelGeneration();
    setIsExportOpen(false);
    setInputs(null);
    setReportData(null);
    setMode(null);
  };

  const backToSetupModal = () => {
    if (mode !== 'generating') {
      return;
    }

    cancelGeneration();
    setIsExportOpen(false);
    setReportData(null);
    setMode('setup');
  };

  const closeReportModal = () => {
    if (mode !== 'report') {
      return;
    }

    cancelGeneration();
    setIsExportOpen(false);
    setInputs(null);
    setReportData(null);
    setMode(null);
  };

  const backFromReportToSetupModal = () => {
    if (mode !== 'report') {
      return;
    }

    cancelGeneration();
    setIsExportOpen(false);
    setMode('setup');
  };

  return (
    <>
      {children(openPrompt)}

      <AnalyticsBuilderSetupModal
        open={mode === 'setup'}
        template={resolvedPrompt}
        inputs={inputs}
        onClose={closeSetupModal}
        onGenerate={generateReport}
      />

      <AnalyticsBuilderGenerateModal
        open={mode === 'generating' && Boolean(inputs)}
        template={resolvedPrompt}
        reportData={reportData}
        onClose={closeGenerateModal}
        onBack={backToSetupModal}
      />

      <AnalyticsBuilderReportModal
        open={mode === 'report' && Boolean(reportData)}
        reportData={reportData}
        onClose={closeReportModal}
        onBack={backFromReportToSetupModal}
        onExport={() => setIsExportOpen(true)}
      />

      <AnalyticsBuilderExportModal open={mode === 'report' && isExportOpen} onClose={() => setIsExportOpen(false)} />
    </>
  );
};

export default AnalyticsBuilder;
